import React, { useEffect, useState } from "react";
import { growthApi } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Plus, Trash2, Lightbulb } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const stageColor = {
  IDEA: "bg-slate-100 text-slate-600 border-slate-200",
  DRAFT: "bg-blue-100 text-blue-700 border-blue-200",
  "FACT CHECK": "bg-amber-100 text-amber-700 border-amber-200",
  "ADMIN APPROVED": "bg-violet-100 text-violet-700 border-violet-200",
  PUBLISHED: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const SUGGESTED = [
  { title: "DODD provider guide for families new to waivers", keyword: "DODD provider Ohio" },
  { title: "What is participant-directed HPC in Ohio?", keyword: "participant directed HPC Ohio" },
  { title: "Can a family member be a caregiver in Ohio? (SELF & IO waivers)", keyword: "can family member be caregiver Ohio" },
  { title: "Non-medical transportation for DODD individuals — how it works", keyword: "DODD transportation provider Ohio" },
  { title: "Respite care options for Ohio family caregivers", keyword: "respite care Ohio" },
];

const ContentIdeasPanel = () => {
  const { toast } = useToast();
  const [ideas, setIdeas] = useState([]);
  const [stages, setStages] = useState([]);
  const [draft, setDraft] = useState({ title: "", keyword: "", county: "" });

  const load = () => growthApi.contentList().then((r) => { setIdeas(r.data.ideas); setStages(r.data.stages); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (payload) => {
    if (!payload.title.trim()) return;
    try {
      const res = await growthApi.contentCreate(payload);
      setIdeas((prev) => [res.data, ...prev]);
      setDraft({ title: "", keyword: "", county: "" });
    } catch {
      toast({ title: "Could not add idea", variant: "destructive" });
    }
  };

  const setStage = async (id, stage) => {
    setIdeas((prev) => prev.map((i) => (i.id === id ? { ...i, stage } : i)));
    try { await growthApi.contentUpdate(id, { stage }); } catch { load(); }
  };

  const remove = async (id) => {
    try {
      await growthApi.contentRemove(id);
      setIdeas((prev) => prev.filter((i) => i.id !== id));
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  return (
    <div data-testid="content-ideas-panel" className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <h3 className="font-display font-bold text-[#0E2A33] mb-1">Content Opportunities</h3>
        <p className="text-sm text-slate-500 mb-4">
          Workflow: IDEA → DRAFT → FACT CHECK → ADMIN APPROVED → PUBLISHED. Nothing is auto-published —
          every piece needs your fact check and approval. Once Google Search Console is connected, query data
          (positions 5–20, high-impression/low-click) will feed this list.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div className="lg:col-span-2">
            <Input data-testid="content-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Content idea / working title" className="h-9" />
          </div>
          <Input value={draft.keyword} onChange={(e) => setDraft({ ...draft, keyword: e.target.value })} placeholder="Target keyword" className="h-9" />
          <div className="flex gap-2">
            <Input value={draft.county} onChange={(e) => setDraft({ ...draft, county: e.target.value })} placeholder="County (optional)" className="h-9" />
            <Button data-testid="content-add-btn" onClick={() => add(draft)} className="bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full h-9 px-4"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {ideas.map((i) => (
          <div key={i.id} className="bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-medium text-[#0E2A33] text-sm">{i.title}</div>
              <div className="text-xs text-slate-400 mt-0.5">{i.keyword && `🔍 ${i.keyword}`} {i.county && ` · ${i.county} County`}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={`border text-[11px] ${stageColor[i.stage]}`}>{i.stage}</Badge>
              <Select value={i.stage} onValueChange={(v) => setStage(i.id, v)}>
                <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="icon" variant="outline" className="h-8 w-8 text-red-600 border-red-200" onClick={() => remove(i.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      {ideas.length === 0 && (
        <div className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-5">
          <h4 className="font-semibold text-[#0E2A33] text-sm flex items-center gap-2 mb-3"><Lightbulb className="h-4 w-4 text-amber-500" /> Suggested starters (high-intent Ohio keywords)</h4>
          <div className="space-y-2">
            {SUGGESTED.map((s) => (
              <div key={s.title} className="flex items-center justify-between gap-2 bg-white rounded-lg border border-slate-100 px-3 py-2">
                <div className="text-sm text-slate-700">{s.title} <span className="text-xs text-slate-400">— {s.keyword}</span></div>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => add({ ...s, county: "" })}>Add</Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentIdeasPanel;
